import { formatMoney } from '@theme/money-formatting';

// Keep sidebar choice outside the replaced section so filtering does not reset it.
const collapsed = new Set();
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pebble-filter-toggle]');
  if (!button) return;
  const root = button.closest('.pebble-main-collection');
  const id = button.getAttribute('aria-controls');
  const hide = button.getAttribute('aria-expanded') === 'true';
  hide ? collapsed.add(id) : collapsed.delete(id);
  root.classList.toggle('pebble-filters-collapsed', hide);
  button.setAttribute('aria-expanded', String(!hide));
});
// The native section renderer morphs toolbar attributes on every result update.
const toolbarObserver = new MutationObserver(() => {
  document.querySelectorAll('[data-pebble-filter-toggle]').forEach((button) => {
    const hide = collapsed.has(button.getAttribute('aria-controls'));
    const root = button.closest('.pebble-main-collection');
    if (root.classList.contains('pebble-filters-collapsed') !== hide) root.classList.toggle('pebble-filters-collapsed', hide);
    if (button.getAttribute('aria-expanded') !== String(!hide)) button.setAttribute('aria-expanded', String(!hide));
  });
});
document.querySelectorAll('.pebble-main-collection').forEach(root => toolbarObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded'] }));

class PebblePriceRange extends HTMLElement {
  static observedAttributes = ['data-minimum', 'data-maximum', 'data-limit'];
  connectedCallback() {
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);
    // Horizon's text-input key guard must not swallow Home/End/PageUp/PageDown.
    this.addEventListener('keydown', this.onKeyDown);
    queueMicrotask(() => this.sync());
  }
  disconnectedCallback() {
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('keydown', this.onKeyDown);
  }
  attributeChangedCallback() { queueMicrotask(() => this.isConnected && this.sync()); }
  get bounds() { return [...this.querySelectorAll('input')]; }
  sync() {
    const [min, max] = this.bounds;
    if (!min || !max) return;
    min.value = this.dataset.minimum;
    max.value = this.dataset.maximum;
    this.paint();
  }
  paint() {
    const [min, max] = this.bounds;
    const limit = Number(this.dataset.limit);
    this.style.setProperty('--range-start', `${limit ? Number(min.value) / limit * 100 : 0}%`);
    this.style.setProperty('--range-end', `${limit ? Number(max.value) / limit * 100 : 100}%`);
    const facet = this.closest('price-facet-component');
    const format = facet.dataset.moneyFormat.match(/{{\s*\w+\s*}}/)?.[0] || '{{amount}}';
    for (const input of this.bounds) {
      input.disabled = limit <= 0;
      input.setAttribute('aria-valuetext', formatMoney(Number(input.value), format, facet.dataset.currency));
    }
  }
  onKeyDown = (event) => event.stopPropagation();
  onInput = (event) => {
    event.stopPropagation();
    const [min, max] = this.bounds;
    if (Number(min.value) > Number(max.value)) {
      if (event.target === min) min.value = max.value;
      else max.value = min.value;
    }
    this.paint();
    const facet = this.closest('price-facet-component');
    const format = facet.dataset.moneyFormat.match(/{{\s*\w+\s*}}/)?.[0] || '{{amount}}';
    const low = facet.querySelector('[ref="minInput"]');
    const high = facet.querySelector('[ref="maxInput"]');
    low.value = Number(min.value) === 0 ? '' : formatMoney(Number(min.value), format, facet.dataset.currency);
    high.value = Number(max.value) === Number(this.dataset.limit) ? '' : formatMoney(Number(max.value), format, facet.dataset.currency);
    low.dataset.max = formatMoney(Number(max.value), format, facet.dataset.currency);
    high.dataset.min = formatMoney(Number(min.value), format, facet.dataset.currency);
  };
  onChange = (event) => {
    // One update on release, not a network request for every pointer movement.
    this.onInput(event);
    this.closest('price-facet-component').updatePriceFilterAndResults();
  };
}
if (!customElements.get('pebble-price-range')) customElements.define('pebble-price-range', PebblePriceRange);
